import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, Loader2, Check, TriangleAlert, ChevronDown, Trophy, Info, Wand2, RefreshCw } from "lucide-react";

const BR_LABELS = { skills: "Zručnosti", experience: "Skúsenosti", responsibilities: "Zodpovednosti", education: "Vzdelanie", other: "Ostatné" };

export default function JobMatches({ jobId }) {
  const nav = useNavigate();
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [disclaimer, setDisclaimer] = useState("AI odporúčanie – nie rozhodnutie o prijatí alebo neprijatí.");

  useEffect(() => {
    api.get(`/company/jobs/${jobId}/matches`).then((r) => { setResults(r.data.results); if (r.data.disclaimer) setDisclaimer(r.data.disclaimer); }).catch(() => setResults([]));
  }, [jobId]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/company/jobs/${jobId}/match`);
      setResults(data.results);
      setDisclaimer(data.disclaimer);
      if (data.results.length === 0) toast.info("V databáze nie sú vhodní (aktívni) kandidáti.");
      else toast.success(`Nájdených ${data.results.length} najvhodnejších kandidátov`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const color = (s) => (s >= 90 ? "text-emerald-600" : s >= 60 ? "text-blue-700" : "text-amber-500");

  return (
    <div className="mt-8 max-w-2xl" data-testid="job-matches">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-head text-xl font-semibold flex items-center gap-2"><Sparkles size={18} className="text-blue-700" /> Nájsť vhodného kandidáta</h2>
        <button data-testid="run-job-match-btn" onClick={run} disabled={running}
          className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-60">
          {running ? <><Loader2 className="animate-spin" size={16} /> AI páruje…</> : results?.length ? <><RefreshCw size={16} /> Prepočítať</> : <><Wand2 size={16} /> Nájsť vhodného kandidáta</>}
        </button>
      </div>

      <p className="mt-2 text-sm text-slate-500">Systém automaticky použije požiadavky z tejto ponuky (názov, zručnosti, prax, vzdelanie, lokalita) a zoradí kandidátov z databázy podľa skóre zhody.</p>

      <div className="mt-3 flex items-start gap-2 rounded-2xl bg-blue-50 border border-blue-100 p-3 text-xs text-blue-800" data-testid="match-disclaimer">
        <Info size={15} className="flex-shrink-0 mt-0.5" /> {disclaimer}
      </div>

      {results === null ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-blue-700" /></div>
      ) : results.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500">Zatiaľ žiadne výsledky. Kliknite na „Nájsť vhodného kandidáta".</p>
      ) : (
        <div className="mt-4 space-y-3" data-testid="match-results">
          {results.map((r, i) => (
            <div key={r.id} data-testid={`match-${r.id}`} className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-3">
                {i === 0 && <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><Trophy size={16} className="text-amber-500" /></div>}
                <button onClick={() => nav(`/company/candidate/${r.id}`)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  {r.photo_url ? <img src={r.photo_url} alt={r.name} className="h-11 w-11 rounded-full object-cover" /> : <div className="h-11 w-11 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-400">{(r.name||"?").charAt(0)}</div>}
                  <div className="min-w-0"><div className="font-head font-semibold truncate">{r.name}</div><div className="text-sm text-slate-500 truncate">{r.title}</div></div>
                </button>
                <div className="text-right"><div className={`font-head text-2xl font-bold ${color(r.score)}`}>{r.score}%</div><div className="text-[10px] text-slate-400 uppercase">zhoda</div></div>
              </div>

              <div className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1">
                {(r.strengths || []).slice(0, 4).map((s, j) => <div key={j} className="flex gap-1.5 text-sm text-slate-700"><Check size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />{s}</div>)}
                {(r.missing || []).slice(0, 3).map((s, j) => <div key={j} className="flex gap-1.5 text-sm text-slate-500"><TriangleAlert size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />{s}</div>)}
              </div>

              <button data-testid={`why-${r.id}`} onClick={() => setOpenId(openId === r.id ? null : r.id)}
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline">
                Prečo toto skóre? <ChevronDown size={15} className={`transition-transform ${openId === r.id ? "rotate-180" : ""}`} />
              </button>

              {openId === r.id && (
                <div className="mt-3 rounded-2xl bg-slate-50 border border-slate-100 p-4" data-testid={`why-panel-${r.id}`}>
                  {r.explanation && <p className="text-sm text-slate-600 mb-3">{r.explanation}</p>}
                  <div className="space-y-2">
                    {Object.entries(r.breakdown || {}).map(([k, v]) => (
                      <div key={k}>
                        <div className="flex justify-between text-xs mb-0.5"><span className="text-slate-600 font-medium">{BR_LABELS[k] || k}</span><span className="text-slate-400">{v}%</span></div>
                        <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${v}%` }} /></div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-[11px] text-slate-400">Chýbajúce požiadavky sú odporúčaním na rozvoj, nie dôvodom na automatické zamietnutie.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
